/**
 * Upload/delete controls for a single module's reference PDF.
 *
 * Handles the presigned URL upload flow for per-module reference PDFs,
 * showing upload/delete/view controls based on current state.
 */

import { useState, useRef } from 'react';
import { Upload, Trash2, FileText, ExternalLink, Loader2 } from 'lucide-react';
import { orgTemplatesApi } from '../../services/api';
import {
  useUploadModulePdf,
  useDeleteModulePdf,
} from '../../hooks/useOrgTemplatePdf';
import { validatePdfFile } from '../../utils/validatePdfFile';
import Button from '../common/Button';

const MODULE_PDF_MAX_BYTES = 10 * 1024 * 1024; // 10MB

interface ModulePdfUploadProps {
  templateId: string;
  moduleId: string; // 'contract-details' or actual module ID
  label: string;
  currentKey?: string;
  isContractDetails?: boolean;
}

export default function ModulePdfUpload({
  templateId,
  moduleId,
  label,
  currentKey,
  isContractDetails = false,
}: ModulePdfUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loadingView, setLoadingView] = useState(false);

  const uploadMutation = useUploadModulePdf(templateId);
  const deleteMutation = useDeleteModulePdf(templateId);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const error = validatePdfFile(file, MODULE_PDF_MAX_BYTES);
      if (error) {
        alert(error);
        return;
      }
      uploadMutation.mutate({ moduleId, file, isContractDetails });
    }
    // Reset input so same file can be selected again
    event.target.value = '';
  };

  const handleView = async () => {
    setLoadingView(true);
    try {
      const result = isContractDetails
        ? await orgTemplatesApi.getContractDetailsPdfUrl(templateId)
        : await orgTemplatesApi.getModulePdfUrl(templateId, moduleId);
      if (result?.url) {
        window.open(result.url, '_blank');
      }
    } finally {
      setLoadingView(false);
    }
  };

  const handleDelete = () => {
    if (confirm(`Delete the reference PDF for "${label}"?`)) {
      deleteMutation.mutate({ moduleId, isContractDetails });
    }
  };

  const isLoading = uploadMutation.isPending || deleteMutation.isPending;

  return (
    <div className="flex items-center justify-between py-3 px-4 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${currentKey ? 'bg-brand-50 dark:bg-brand-900/20' : 'bg-slate-100 dark:bg-slate-700'}`}>
          <FileText
            className={`h-5 w-5 ${currentKey ? 'text-brand-600 dark:text-brand-400' : 'text-slate-400'}`}
          />
        </div>
        <div>
          <p className="text-body font-medium text-slate-900 dark:text-slate-100">{label}</p>
          <p className="text-caption text-slate-500 dark:text-slate-400">
            {currentKey ? 'PDF uploaded' : 'No PDF uploaded'}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {currentKey && (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleView}
              disabled={loadingView}
              icon={
                loadingView ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ExternalLink className="h-4 w-4" />
                )
              }
            >
              View
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDelete}
              disabled={isLoading}
              icon={<Trash2 className="h-4 w-4" />}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20"
            >
              Delete
            </Button>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf"
          onChange={handleFileSelect}
          className="hidden"
        />
        <Button
          variant={currentKey ? 'secondary' : 'primary'}
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={isLoading}
          icon={
            isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )
          }
        >
          {currentKey ? 'Replace' : 'Upload'}
        </Button>
      </div>
    </div>
  );
}
